import { Portal } from '@angular/cdk/portal';
import { Injectable, signal } from '@angular/core';

@Injectable({
    providedIn: 'root'
})
export class DialogService {
    readonly headerPortal = signal<Portal<unknown> | null>(null);
    readonly contentPortal = signal<Portal<unknown> | null>(null);
    readonly footerPortal = signal<Portal<unknown> | null>(null);
    readonly dialogVisible = signal<boolean>(false);
    readonly dialogTitle = signal<string>('');
    readonly dialogWidth = signal<string>('');
    readonly dialogSize = signal<FcpDialogSize>(FcpDialogSize.Medium);
    readonly dialogMaximizable = signal<boolean>(false);
    readonly backdrop = signal<boolean>(true);
    readonly e2eHook = signal<string | null>(null);
    readonly headless = signal<boolean>(false);
    readonly headlessContent = signal<Portal<unknown> | null>(null);

    setE2eHook(e2eHook: string | null): void {
        this.e2eHook.set(e2eHook);
    }

    setDialogMaximizable(maximizable: boolean): void {
        this.dialogMaximizable.set(maximizable);
    }

    setBackdrop(backdrop: boolean): void {
        this.backdrop.set(backdrop);
    }

    setDialogTitle(title: string): void {
        this.dialogTitle.set(title);
    }

    setDialogWidth(width: string): void {
        this.dialogWidth.set(width);
    }

    setDialogSize(size: FcpDialogSize): void {
        this.dialogSize.set(size);
    }

    setHeaderPortal(portal: Portal<unknown> | null): void {
        this.disposePortal(this.headerPortal());
        this.headerPortal.set(portal);
    }

    setContentPortal(portal: Portal<unknown> | null): void {
        this.disposePortal(this.contentPortal());
        this.contentPortal.set(portal);
    }

    setFooterPortal(portal: Portal<unknown> | null): void {
        this.disposePortal(this.footerPortal());
        this.footerPortal.set(portal);
    }

    setHeadlessContent(portal: Portal<unknown> | null): void {
        this.disposePortal(this.headlessContent());
        this.headlessContent.set(portal);
        this.headless.set(portal !== null);
    }

    open(): void {
        this.dialogVisible.set(true);
    }

    /**
     * Clears any previous content, applies the given config, and opens the dialog.
     * Use this to show different content each time; content is also cleared when the dialog is closed.
     */
    openWithConfig(config: DialogConfig): void {
        this.clearContent();
        this.applyConfig(config);
        this.dialogVisible.set(true);
    }

    /** Closes the dialog and clears projected content so the next open shows fresh content. */
    close(): void {
        this.clearContent();
        this.dialogVisible.set(false);
    }

    clear(): void {
        this.clearContent();
        this.dialogVisible.set(false);
    }

    /** Clears portals and resets dialog state without changing visibility. */
    private clearContent(): void {
        this.disposePortal(this.headerPortal());
        this.disposePortal(this.contentPortal());
        this.disposePortal(this.footerPortal());
        this.disposePortal(this.headlessContent());
        this.headerPortal.set(null);
        this.contentPortal.set(null);
        this.footerPortal.set(null);
        this.headlessContent.set(null);
        this.headless.set(false);
        this.dialogWidth.set('');
        this.dialogSize.set(FcpDialogSize.Medium);
        this.dialogMaximizable.set(false);
        this.backdrop.set(true);
        this.e2eHook.set(null);
        this.dialogTitle.set('');
    }

    private applyConfig(config: DialogConfig): void {
        if (config.title !== undefined) this.dialogTitle.set(config.title);
        if (config.size !== undefined) this.dialogSize.set(config.size);
        if (config.width !== undefined) this.dialogWidth.set(config.width);
        if (config.maximizable !== undefined) this.dialogMaximizable.set(config.maximizable);
        if (config.backdrop !== undefined) this.backdrop.set(config.backdrop);
        if (config.e2eHook !== undefined) this.e2eHook.set(config.e2eHook);

        if (config.headlessContent !== undefined) {
            this.setHeadlessContent(config.headlessContent);
        } else {
            if (config.headerPortal !== undefined) this.setHeaderPortal(config.headerPortal);
            if (config.contentPortal !== undefined) this.setContentPortal(config.contentPortal);
            if (config.footerPortal !== undefined) this.setFooterPortal(config.footerPortal);
        }
    }

    private disposePortal(portal: Portal<unknown> | null): void {
        if (portal && portal.isAttached) {
            portal.detach();
        }
    }
}

export enum FcpDialogSize {
    Small = 'small',
    Medium = 'medium',
    Large = 'large'
}

export interface DialogConfig {
    title?: string;
    size?: FcpDialogSize;
    width?: string;
    maximizable?: boolean;
    backdrop?: boolean;
    e2eHook?: string | null;
    headerPortal?: Portal<unknown> | null;
    contentPortal?: Portal<unknown> | null;
    footerPortal?: Portal<unknown> | null;
    headlessContent?: Portal<unknown> | null;
}
